import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Truck, XCircle } from "lucide-react";

export default function Claim() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    api.get(`/claim/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/claim/${token}`, { password });
      localStorage.setItem("gr_token", data.token);
      await refreshUser();
      toast.success(`Welcome to FlavorFlow, ${data.user.name}!`);
      navigate("/dashboard/owner");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  if (info === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-4 border-teal-200 border-t-[#0FB5A6] animate-spin" /></div>;

  if (info === null) return (
    <div className="max-w-md mx-auto px-5 py-24 text-center">
      <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
      <h1 className="font-display text-3xl font-black mt-4">Invalid or used link</h1>
      <p className="text-slate-500 mt-2">This claim link is no longer valid. If you already claimed your truck, just log in.</p>
      <Button onClick={() => navigate("/login")} className="mt-6 rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="claim-login-btn">Log in</Button>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <div className="flex items-center gap-2 justify-center mb-6">
        <span className="w-10 h-10 rounded-full bg-[#0FB5A6] flex items-center justify-center"><Truck className="w-5 h-5 text-white" /></span>
        <span className="font-display font-extrabold text-2xl">FlavorFlow</span>
      </div>
      <div className="bg-white rounded-2xl border border-teal-100 p-8 gr-shadow">
        <span className="inline-block px-3 py-1 rounded-full bg-[#F2635F] text-white text-xs font-bold uppercase tracking-widest">Founding 100</span>
        <h1 className="font-display text-3xl font-black mt-3">Claim {info.truck_name}</h1>
        <p className="text-slate-500 mt-1">Welcome, {info.owner_name}! Set a password to activate your owner account. We've pre-loaded your truck ({info.cuisine} · {info.city}).</p>
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="claim-form">
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={info.email} disabled data-testid="claim-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Choose a password</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="claim-password" />
          </div>
          <Button type="submit" disabled={submitting} className="w-full rounded-full h-12 font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white active:scale-95 transition-transform" data-testid="claim-submit">
            {submitting ? "Setting up…" : "Claim my truck & log in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
